import React from "react";
import { graphql, PageProps as GatsbyPageProps } from "gatsby";
import { PageQuery } from "../../gatsby-graphql";
import { ModulesRenderer } from "../carlsberg-headless/ModulesRenderer";
import { SiteProvider } from "../context/site/SiteProvider";
import { LinkCollection } from "../carlsberg-headless/types/LinkCollection";
import { PageLayout } from "../shared/layouts/Page/PageLayout";
import { Header } from "@modules/Header/Header";
import { Navigation } from "@modules/Navigation/Navigation";
import { Footer } from "@modules/Footer/Footer";
import { DictionaryProvider } from "../context/dictionary/DictionaryProvider";
import { BaseResponse } from "@akqa_denmark/cb-sdk/build/types/base-response";
import { GlobalStyles } from "@styles/GlobalStyles";
import { DefaultMeta } from "../carlsberg-headless/types/DefaultMeta";
import { SeoMeta } from "../carlsberg-headless/types/SeoMeta";
import { SEOMeta } from "@shared-components/SeoMeta/SeoMeta";
import { OpenGraph } from "@shared-components/OpenGraph/OpenGraph";
import { Favicon } from "@shared-components/Favicon/Favicon";
import { GoogleMapInstanceProvider } from "../context/googleMapInstance/GoogleMapInstanceProvider";
import { NavigationProvider } from "../context/navigation/NavigationProvider";
import { TaxonomiesField } from "../carlsberg-headless/types/fields/Taxonomies";
import { ComplianceScript } from "../carlsberg-headless/CompliancePack";

export interface PageContext {
  relativeUrl: string;
  currentCulture: string;
  cultureSelector: BaseResponse<LinkCollection>;
  cbId: string | number;
}

export type PageProps = GatsbyPageProps<PageQuery, PageContext>;

const Page: React.FC<PageProps> = ({ data, pageContext }) => {
  const [heroModule, ...restModules] = data.page?.content?.modules;

  const navigationMain = data.navigationMain?.content;
  const footer1stColumn = data.footer1stColumn?.content;
  const footer2ndColumn = data.footer2ndColumn?.content;
  const footer3rdColumn = data.footer3rdColumn?.content;
  const navigationSocialLinks = data.navigationSocialLinks?.content;

  const siteProviderData = {
    cultures: data.site?.content?.cultures,
    currentCulture: data.site?.meta?.currentCulture as string,
    currentFrontpageUrl: data.site?.content?.pages?.relativeUrl,
    settings: data.site?.content?.settings,
    bookTicketLink: data.bookTicketLink?.content as LinkCollection,
    buyGiftcardLink: data.buyGiftcardLink?.content as LinkCollection,
    cultureSelector: pageContext.cultureSelector.content,
    pageTaxonomies: data?.page?.content?.taxonomies as TaxonomiesField,
    hocFlavourIds: null,
  };

  return (
    <SiteProvider data={siteProviderData}>
      <DictionaryProvider data={{ dictionary: data.dictionary?.content }}>
        <GoogleMapInstanceProvider>
          <NavigationProvider
            linkCollection={navigationMain as LinkCollection}
            cultureSelector={
              pageContext.cultureSelector.content as LinkCollection
            }
          >
            <ComplianceScript />
            <GlobalStyles />
            <Favicon favicon={data.site?.content?.settings?.favicon} />
            <SEOMeta
              defaultMeta={data.page?.meta as DefaultMeta}
              seoMeta={data.page?.content?.meta as SeoMeta}
            />
            <OpenGraph
              defaultMeta={data.page?.meta as DefaultMeta}
              seoMeta={data.page?.content?.meta as SeoMeta}
            />
            <PageLayout
              header={
                <Header
                  navigation={<Navigation />}
                  companyLogoPrimary={
                    data.site?.content?.settings?.companyLogoPrimary
                  }
                />
              }
              footer={
                <Footer
                  linkCollection1stColumn={footer1stColumn as LinkCollection}
                  linkCollection2ndColumn={footer2ndColumn as LinkCollection}
                  linkCollection3rdColumn={footer3rdColumn as LinkCollection}
                  socialLinks={navigationSocialLinks as LinkCollection}
                />
              }
              {...(heroModule && {
                hero: (
                  <ModulesRenderer
                    modules={[heroModule]}
                    disableLayoutRendering
                  />
                ),
              })}
            >
              {restModules.length > 0 && (
                <ModulesRenderer modules={restModules} />
              )}
            </PageLayout>
          </NavigationProvider>
        </GoogleMapInstanceProvider>
      </DictionaryProvider>
    </SiteProvider>
  );
};

export const pageQuery = graphql`
  query Page($cbId: String!, $currentCulture: String!) {
    page: carlsbergHeadlessPage(cbId: { eq: $cbId }) {
      meta {
        currentCulture
        defaultTitle
        postfixTitle
        prefixTitle
        ogDefaultTitle
        ogDefaultDescription
      }
      content {
        modules
        id
        taxonomies
        meta {
          titleTag
          metaDescription
          robotIndexing
          robotFollow
          hideFromSitemap
          openGraphTitle
          openGraphDescription
          openGraphImage {
            alias
            url
            height
            width
            extension
            size
            fullUrl
            altText
          }
        }
      }
    }

    site: carlsbergHeadlessSite(
      meta: { currentCulture: { eq: $currentCulture } }
    ) {
      cbId
      meta {
        currentCulture
      }
      content {
        name
        settings
        cultures
        pages
      }
    }

    navigationMain: carlsbergHeadlessLinkCollection(
      meta: { currentCulture: { eq: $currentCulture } }
      content: { taxonomies: { in: "navigation_main" } }
    ) {
      meta {
        currentCulture
      }
      content {
        name
        links
        themes
        taxonomies
        shortCodes
      }
    }

    footer1stColumn: carlsbergHeadlessLinkCollection(
      meta: { currentCulture: { eq: $currentCulture } }
      content: { taxonomies: { in: "footer_1st_column" } }
    ) {
      meta {
        currentCulture
      }
      content {
        name
        links
        themes
        taxonomies
        shortCodes
      }
    }

    footer2ndColumn: carlsbergHeadlessLinkCollection(
      meta: { currentCulture: { eq: $currentCulture } }
      content: { taxonomies: { in: "footer_2nd_column" } }
    ) {
      meta {
        currentCulture
      }
      content {
        name
        links
        themes
        taxonomies
        shortCodes
      }
    }

    footer3rdColumn: carlsbergHeadlessLinkCollection(
      meta: { currentCulture: { eq: $currentCulture } }
      content: { taxonomies: { in: "footer_3rd_column" } }
    ) {
      meta {
        currentCulture
      }
      content {
        name
        links
        themes
        taxonomies
        shortCodes
      }
    }

    navigationSocialLinks: carlsbergHeadlessLinkCollection(
      meta: { currentCulture: { eq: $currentCulture } }
      content: { taxonomies: { in: "navigation_social_links" } }
    ) {
      meta {
        currentCulture
      }
      content {
        name
        links
        themes
        taxonomies
        shortCodes
      }
    }

    bookTicketLink: carlsbergHeadlessLinkCollection(
      meta: { currentCulture: { eq: $currentCulture } }
      content: { taxonomies: { in: "link_collection_book_ticket" } }
    ) {
      meta {
        currentCulture
      }
      content {
        name
        links
        themes
        taxonomies
        shortCodes
      }
    }

    buyGiftcardLink: carlsbergHeadlessLinkCollection(
      meta: { currentCulture: { eq: $currentCulture } }
      content: { taxonomies: { in: "link_collection_buy_giftcard" } }
    ) {
      meta {
        currentCulture
      }
      content {
        name
        links
        themes
        taxonomies
        shortCodes
      }
    }

    dictionary: carlsbergHeadlessDictionary {
      content
    }
  }
`;

export default Page;
